import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Link,
  Avatar,
  Stack,
  Grid,
  Alert,
  alpha,
  Tooltip,
  Fade,
  Paper,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  Refresh,
  Circle,
  Close,
  CheckCircle,
  Warning,
  Block,
  Hub,
  Router,
  OpenInNew,
  SignalCellularAlt,
  Lan,
  Map as MapIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ftthCaixaService, FtthCaixa } from '../../services/ftthCaixaService';
import { MapaPontoCto } from '../../services/monitoramentoService';
import equipmentService from '../../services/equipmentService';
import { contractService } from '../../services/contractService';
import ConnectionService from '../../services/connectionService';
import { Contract, Equipment } from '../../types';
import CtosModal from '../../pages/ftth/olt/CtosModal';
import CaixaMapComponent from '../../pages/ftth/components/CaixaMapComponent';
import { useRelatorioCores } from '../../pages/relatorios';
import { formatMonitorDateTime } from '../../utils/monitorDisplayUtils';

const STATUS_COLORS = {
  online: '#4CAF50',
  offline: '#F44336',
  vazia: '#9E9E9E',
} as const;

const getStatusColor = (status: string) => {
  if (status === 'online') return STATUS_COLORS.online;
  if (status === 'offline') return STATUS_COLORS.offline;
  return STATUS_COLORS.vazia;
};

type CtoPortStatus = 'available' | 'occupied' | 'disabled';

interface CtoPortInfo {
  clientName?: string;
  clientId?: number;
  contractId?: number;
  contractTitle?: string;
  serviceId?: number;
  serviceStatus?: string;
  serviceType?: string;
  username?: string;
  connectionStatus?: 'Online' | 'Offline' | 'N/A';
  reason?: string;
  date?: string;
}

interface CtoPort {
  port_number: number;
  status: CtoPortStatus;
  info?: CtoPortInfo;
}

const normalizeStatusLabel = (status?: string) => {
  if (!status) return 'N/A';
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getPortStatusMeta = (status: CtoPortStatus) => {
  switch (status) {
    case 'available':
      return { label: 'Disponivel', color: 'success' as const, icon: <CheckCircle color="success" fontSize="small" /> };
    case 'occupied':
      return { label: 'Ocupada', color: 'warning' as const, icon: <Warning color="warning" fontSize="small" /> };
    case 'disabled':
      return { label: 'Desabilitada', color: 'error' as const, icon: <Block color="error" fontSize="small" /> };
    default:
      return { label: 'Desconhecida', color: 'default' as const, icon: <Circle fontSize="small" /> };
  }
};

const getServiceStatusColor = (status?: string) => {
  switch (status) {
    case 'ativo':
      return 'success' as const;
    case 'bloqueado':
    case 'suspenso':
      return 'warning' as const;
    case 'cancelado':
      return 'error' as const;
    case 'aguardando_instalacao':
    case 'aguardando_assinatura':
      return 'info' as const;
    default:
      return 'default' as const;
  }
};

const getConnectionStatusColor = (status?: string) => {
  if (status === 'Online') return 'success' as const;
  if (status === 'Offline') return 'error' as const;
  return 'default' as const;
};

const parseDisabledPorts = (disabledPorts: FtthCaixa['disabled_ports'] | string | undefined | null): any[] => {
  if (!disabledPorts) return [];
  if (Array.isArray(disabledPorts)) return disabledPorts;

  try {
    const parsed = JSON.parse(disabledPorts as string);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const ftthCaixaToCtoSummary = (caixa: FtthCaixa): MapaPontoCto => ({
  id: caixa.id,
  name: caixa.name,
  latitude: caixa.latitude ?? 0,
  longitude: caixa.longitude ?? 0,
  capacidade: caixa.capacidade,
  cto_status: (caixa.monitor_status || caixa.funcionamento || 'vazia') as MapaPontoCto['cto_status'],
  monitor_ultimo_online_em: caixa.monitor_ultimo_online_em,
  monitor_ultimo_offline_em: caixa.monitor_ultimo_offline_em,
});

export interface CtoMonitoramentoDetailsModalProps {
  open: boolean;
  caixaId: number | null;
  onClose: () => void;
  initialCto?: MapaPontoCto | null;
  container?: HTMLElement | null;
}

const CtoMonitoramentoDetailsModal: React.FC<CtoMonitoramentoDetailsModalProps> = ({
  open,
  caixaId,
  onClose,
  initialCto = null,
  container,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const relatorioCores = useRelatorioCores();

  const [selectedCto, setSelectedCto] = useState<MapaPontoCto | null>(null);
  const [selectedCaixa, setSelectedCaixa] = useState<FtthCaixa | null>(null);
  const [ctoPorts, setCtoPorts] = useState<CtoPort[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [ctosModalOpen, setCtosModalOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  const portStats = useMemo(() => ({
    available: ctoPorts.filter((p) => p.status === 'available').length,
    occupied: ctoPorts.filter((p) => p.status === 'occupied').length,
    disabled: ctoPorts.filter((p) => p.status === 'disabled').length,
  }), [ctoPorts]);

  const buildCtoPorts = useCallback(async (caixa: FtthCaixa): Promise<CtoPort[]> => {
    const equipmentsResponse = await equipmentService.getEquipments({
      caixa_id: caixa.id,
      limit: 100,
    });

    let equipments: Equipment[] = [];
    if (Array.isArray(equipmentsResponse.data)) {
      equipments = equipmentsResponse.data;
    } else if ((equipmentsResponse.data as any)?.equipments) {
      equipments = (equipmentsResponse.data as any).equipments;
    }

    const contractIds = Array.from(
      new Set(
        equipments
          .map((equipment) => equipment.contrato_id)
          .filter((id): id is number => typeof id === 'number'),
      ),
    );

    const contracts = new Map<number, Contract>();
    await Promise.all(
      contractIds.map(async (contractId) => {
        try {
          const response = await contractService.getById(contractId);
          if (response.success && response.data) {
            contracts.set(contractId, response.data);
          }
        } catch (error) {
          console.warn(`Erro ao buscar contrato ${contractId}`, error);
        }
      }),
    );

    const occupiedPorts = new Map<number, CtoPortInfo>();
    const occupiedEntries = equipments
      .filter((equipment) => !!equipment.caixa_port_number && !!equipment.contrato_id)
      .map((equipment) => {
        const contract = contracts.get(equipment.contrato_id!);
        const service = contract?.servicos_adicionais?.find((item) => item.id === equipment.contrato_servico_id);
        const equipmentServiceInfo = (equipment as any).contrato_servico;

        const info: CtoPortInfo = {
          clientName: contract?.cliente?.nome || equipment.contrato?.cliente?.nome || `Cliente #${equipment.contrato_id}`,
          clientId: contract?.cliente_id || equipment.contrato?.cliente?.id,
          contractId: equipment.contrato_id!,
          contractTitle: contract?.titulo || equipment.contrato?.titulo || `Contrato #${equipment.contrato_id}`,
          serviceId: equipment.contrato_servico_id || service?.id,
          serviceStatus: service?.status || equipmentServiceInfo?.status || contract?.status,
          serviceType: service?.tipo || equipmentServiceInfo?.tipo,
          username: service?.usuario_pppoe || equipmentServiceInfo?.usuario_pppoe,
          connectionStatus: 'N/A',
        };

        return {
          portNumber: equipment.caixa_port_number!,
          contractId: equipment.contrato_id!,
          serviceId: equipment.contrato_servico_id || service?.id,
          info,
        };
      });

    await Promise.all(
      occupiedEntries.map(async (entry) => {
        try {
          const response = await ConnectionService.getByContract(entry.contractId, {
            onlyActive: true,
            limit: 1,
            ...(entry.serviceId ? { serviceId: entry.serviceId } : {}),
          });
          entry.info.connectionStatus = response.data?.length ? 'Online' : 'Offline';
        } catch (error) {
          console.warn(`Erro ao buscar conexao do contrato ${entry.contractId}`, error);
          entry.info.connectionStatus = 'N/A';
        }
      }),
    );

    occupiedEntries.forEach((entry) => {
      occupiedPorts.set(entry.portNumber, entry.info);
    });

    const disabledPorts = parseDisabledPorts(caixa.disabled_ports);
    const totalPorts = caixa.capacidade || 0;

    return Array.from({ length: totalPorts }, (_, index) => {
      const portNumber = index + 1;
      const disabledPort = disabledPorts.find((port: any) => Number(port.port) === portNumber);
      const occupiedInfo = occupiedPorts.get(portNumber);

      if (disabledPort) {
        return {
          port_number: portNumber,
          status: 'disabled' as const,
          info: {
            reason: disabledPort.reason || 'Desabilitada',
            date: disabledPort.date || disabledPort.disabledAt,
          },
        };
      }

      if (occupiedInfo) {
        return {
          port_number: portNumber,
          status: 'occupied' as const,
          info: occupiedInfo,
        };
      }

      return {
        port_number: portNumber,
        status: 'available' as const,
      };
    });
  }, []);

  const loadDetails = useCallback(async (cto: MapaPontoCto) => {
    setSelectedCto(cto);
    setSelectedCaixa(null);
    setCtoPorts([]);
    setDetailsError('');
    setDetailsLoading(true);

    try {
      const caixaResponse = await ftthCaixaService.getById(cto.id);
      if (!caixaResponse.success || !caixaResponse.data) {
        throw new Error('CTO nao encontrada');
      }

      setSelectedCaixa(caixaResponse.data);
      const ports = await buildCtoPorts(caixaResponse.data);
      setCtoPorts(ports);
    } catch (err: any) {
      console.error('Erro ao carregar detalhes da CTO:', err);
      setDetailsError(err.message || 'Erro ao carregar detalhes da CTO');
    } finally {
      setDetailsLoading(false);
    }
  }, [buildCtoPorts]);

  useEffect(() => {
    if (!open || !caixaId) return;

    const cto: MapaPontoCto = initialCto?.id === caixaId
      ? initialCto
      : {
          id: caixaId,
          name: initialCto?.name || `CTO #${caixaId}`,
          latitude: initialCto?.latitude ?? 0,
          longitude: initialCto?.longitude ?? 0,
          capacidade: initialCto?.capacidade ?? null,
          cto_status: initialCto?.cto_status || 'vazia',
          monitor_ultimo_online_em: initialCto?.monitor_ultimo_online_em,
          monitor_ultimo_offline_em: initialCto?.monitor_ultimo_offline_em,
        };

    loadDetails(cto);
    // initialCto é lido apenas na abertura para evitar recarregamentos desnecessários
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caixaId, loadDetails]);

  const handleClose = () => {
    setSelectedCto(null);
    setSelectedCaixa(null);
    setCtoPorts([]);
    setDetailsError('');
    setCtosModalOpen(false);
    setMapModalOpen(false);
    onClose();
  };

  const handleRefresh = () => {
    if (selectedCto) {
      loadDetails(selectedCto);
    }
  };

  const ctoStatus = selectedCto?.cto_status || initialCto?.cto_status || 'vazia';

  const mapCoords = useMemo(() => {
    const lat = selectedCaixa?.latitude ?? initialCto?.latitude;
    const lng = selectedCaixa?.longitude ?? initialCto?.longitude;
    const latitude = lat != null && lat !== '' ? Number(lat) : NaN;
    const longitude = lng != null && lng !== '' ? Number(lng) : NaN;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    return { latitude, longitude };
  }, [selectedCaixa, initialCto]);

  const mapCaixaName = selectedCaixa?.name || selectedCto?.name || initialCto?.name || 'CTO';
  const mapAddress = selectedCaixa?.address || '';

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        container={container}
        PaperProps={{
          sx: { borderRadius: 2, overflow: 'hidden' },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(135deg, ${alpha(relatorioCores.primario, 0.12)} 0%, ${alpha(relatorioCores.secundario, 0.08)} 100%)`,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid item xs={12}>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: alpha(getStatusColor(ctoStatus), 0.15),
                    color: getStatusColor(ctoStatus),
                    fontWeight: 700,
                  }}
                >
                  <Hub sx={{ fontSize: 22 }} />
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap flex={1} minWidth={0}>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                      {selectedCaixa?.id ? (
                        <Link
                          component="button"
                          variant="subtitle1"
                          onClick={() => navigate(`/ftth/caixas/${selectedCaixa.id}`)}
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            fontWeight: 800,
                            fontSize: '1rem',
                            lineHeight: 1.2,
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {selectedCaixa.name || selectedCto?.name || initialCto?.name || 'Detalhes da CTO'}
                        </Link>
                      ) : (
                        selectedCaixa?.name || selectedCto?.name || initialCto?.name || 'Detalhes da CTO'
                      )}
                    </Typography>
                    <Chip
                      label={normalizeStatusLabel(ctoStatus)}
                      size="small"
                      sx={{
                        color: '#fff',
                        bgcolor: getStatusColor(ctoStatus),
                        fontWeight: 700,
                        height: 22,
                        fontSize: '0.7rem',
                      }}
                    />
                    </Stack>
                    <Stack direction="row" spacing={0.25} alignItems="center" flexShrink={0}>
                      <Tooltip title="Atualizar dados">
                        <IconButton
                          onClick={handleRefresh}
                          disabled={detailsLoading || !caixaId}
                          size="small"
                          sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', p: 0.5 }}
                        >
                          {detailsLoading ? <CircularProgress size={16} /> : <Refresh sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                      <IconButton onClick={handleClose} size="small" sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', p: 0.5 }}>
                        <Close sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Stack>
                  </Box>

                  {selectedCaixa?.address && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', lineHeight: 1.3 }}>
                      {selectedCaixa.address}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}>
                        Última vez online
                      </Typography>
                      <Typography variant="caption" fontWeight={600} color="success.main" sx={{ fontSize: '0.78rem' }}>
                        {formatMonitorDateTime(
                          selectedCaixa?.monitor_ultimo_online_em
                            ?? selectedCto?.monitor_ultimo_online_em
                            ?? initialCto?.monitor_ultimo_online_em,
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}>
                        Última vez offline
                      </Typography>
                      <Typography variant="caption" fontWeight={600} color="error.main" sx={{ fontSize: '0.78rem' }}>
                        {formatMonitorDateTime(
                          selectedCaixa?.monitor_ultimo_offline_em
                            ?? selectedCto?.monitor_ultimo_offline_em
                            ?? initialCto?.monitor_ultimo_offline_em,
                        )}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                    {selectedCaixa?.id && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                        onClick={() => navigate(`/ftth/caixas/${selectedCaixa.id}`)}
                        sx={{ borderRadius: 1.5, bgcolor: 'background.paper', py: 0.25, px: 1, fontSize: '0.72rem', minHeight: 28 }}
                      >
                        Pagina da CTO
                      </Button>
                    )}
                    {mapCoords && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setMapModalOpen(true)}
                        sx={{ borderRadius: 1.5, bgcolor: 'background.paper', py: 0.25, px: 1, fontSize: '0.72rem', minHeight: 28 }}
                      >
                        Abrir Mapa
                      </Button>
                    )}
                    {selectedCaixa?.pon_port?.olt?.name && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Router sx={{ fontSize: 14 }} />}
                        onClick={() => navigate(`/ftth/olts/${selectedCaixa.pon_port!.olt!.id}`)}
                        sx={{ borderRadius: 1.5, bgcolor: 'background.paper', py: 0.25, px: 1, fontSize: '0.72rem', minHeight: 28 }}
                      >
                        {selectedCaixa.pon_port.olt.name}
                      </Button>
                    )}
                    {(selectedCaixa?.pon_port?.id || selectedCaixa?.port_id) && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Lan sx={{ fontSize: 14 }} />}
                        onClick={() => setCtosModalOpen(true)}
                        sx={{ borderRadius: 1.5, bgcolor: 'background.paper', py: 0.25, px: 1, fontSize: '0.72rem', minHeight: 28 }}
                      >
                        Porta {selectedCaixa?.pon_port?.sfp || selectedCaixa?.pon_port?.portNumber || selectedCaixa?.port_id}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Stack>

              {!detailsLoading && ctoPorts.length > 0 && (
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {[
                    { label: 'Total', value: ctoPorts.length, color: theme.palette.primary.main },
                    { label: 'Disponiveis', value: portStats.available, color: theme.palette.success.main },
                    { label: 'Ocupadas', value: portStats.occupied, color: theme.palette.warning.main },
                    { label: 'Desabilitadas', value: portStats.disabled, color: theme.palette.error.main },
                  ].map((item) => (
                    <Grid item xs={6} sm={3} key={item.label}>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1.5,
                          bgcolor: alpha(item.color, 0.08),
                          border: '1px solid',
                          borderColor: alpha(item.color, 0.2),
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={800} sx={{ color: item.color, lineHeight: 1, fontSize: '1rem' }}>
                          {item.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.68rem' }}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {detailsLoading && <LinearProgress />}

          <Box sx={{ px: 2, py: 1.5 }}>
            {detailsError && (
              <Alert severity="error" sx={{ mb: 1, py: 0.25, borderRadius: 1.5, '& .MuiAlert-message': { py: 0.25 } }}>
                {detailsError}
              </Alert>
            )}

            {selectedCaixa && !selectedCaixa.pon_port && !detailsLoading && (
              <Alert severity="info" sx={{ mb: 1, py: 0.25, borderRadius: 1.5, '& .MuiAlert-message': { py: 0.25 } }} icon={<SignalCellularAlt fontSize="small" />}>
                Porta PON nao vinculada a esta CTO
              </Alert>
            )}

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: 340,
                borderRadius: 1.5,
                boxShadow: theme.shadows[1],
                '& .MuiTable-root': { minWidth: 650 },
              }}
            >
              <Table stickyHeader size="small" sx={{ '& .MuiTableCell-sizeSmall': { py: 0.5, px: 1 } }}>
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: (t) => `color-mix(in srgb, ${theme.palette.primary.main} 18%, ${t.palette.background.default})`,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                    }}
                  >
                    <TableCell align="center" sx={{ width: 80 }}>Porta</TableCell>
                    <TableCell sx={{ width: 140 }}>Status</TableCell>
                    <TableCell>Cliente / Contrato</TableCell>
                    <TableCell sx={{ width: 180 }}>Status do Servico</TableCell>
                    <TableCell sx={{ width: 150 }}>Conexao</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ctoPorts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Stack alignItems="center" spacing={0.5}>
                          <Lan sx={{ fontSize: 32, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            {detailsLoading ? 'Carregando portas...' : 'Nenhuma porta encontrada'}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    ctoPorts.map((port) => {
                      const statusMeta = getPortStatusMeta(port.status);

                      return (
                        <TableRow
                          key={port.port_number}
                          hover
                          sx={{
                            '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                          }}
                        >
                          <TableCell align="center">
                            <Avatar
                              sx={{
                                width: 26,
                                height: 26,
                                mx: 'auto',
                                fontSize: '0.72rem',
                                fontWeight: 800,
                                bgcolor: port.status === 'available'
                                  ? 'success.main'
                                  : port.status === 'occupied'
                                    ? 'warning.main'
                                    : 'error.main',
                                boxShadow: 1,
                              }}
                            >
                              {port.port_number}
                            </Avatar>
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {statusMeta.icon}
                              <Chip label={statusMeta.label} color={statusMeta.color} size="small" variant="outlined" sx={{ fontWeight: 600, height: 22, fontSize: '0.68rem' }} />
                            </Stack>
                          </TableCell>

                          <TableCell>
                            {port.status === 'occupied' ? (
                              <>
                                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.78rem', lineHeight: 1.25 }}>
                                  {port.info?.clientId ? (
                                    <Link
                                      component="button"
                                      variant="body2"
                                      onClick={() => navigate(`/clients/${port.info?.clientId}`)}
                                      sx={{ textDecoration: 'none', textAlign: 'left', fontWeight: 600 }}
                                    >
                                      {port.info.clientName}
                                    </Link>
                                  ) : (
                                    port.info?.clientName || 'Cliente'
                                  )}
                                </Typography>
                                {port.info?.contractId && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Contrato:{' '}
                                    <Link
                                      component="button"
                                      variant="caption"
                                      onClick={() => navigate(`/contracts/${port.info?.contractId}`)}
                                      sx={{ textDecoration: 'none', color: 'inherit', fontWeight: 600 }}
                                    >
                                      {port.info.contractTitle}
                                    </Link>
                                  </Typography>
                                )}
                              </>
                            ) : port.status === 'disabled' ? (
                              <>
                                <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.25 }}>{port.info?.reason || 'Desabilitada'}</Typography>
                                {port.info?.date && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {new Date(port.info.date).toLocaleString()}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>Disponivel</Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {port.status === 'occupied' ? (
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip
                                  label={normalizeStatusLabel(port.info?.serviceStatus)}
                                  color={getServiceStatusColor(port.info?.serviceStatus)}
                                  size="small"
                                  sx={{ fontWeight: 600, height: 22, fontSize: '0.68rem' }}
                                />
                                {port.info?.serviceType && (
                                  <Typography variant="caption" color="text.secondary">
                                    {normalizeStatusLabel(port.info.serviceType)}
                                  </Typography>
                                )}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>-</Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {port.status === 'occupied' ? (
                              <Chip
                                label={port.info?.connectionStatus || 'N/A'}
                                color={getConnectionStatusColor(port.info?.connectionStatus)}
                                size="small"
                                variant={port.info?.connectionStatus === 'N/A' ? 'outlined' : 'filled'}
                                sx={{ fontWeight: 700, minWidth: 64, height: 22, fontSize: '0.68rem' }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
      </Dialog>

      {mapCoords && (
        <Dialog
          open={mapModalOpen}
          onClose={() => setMapModalOpen(false)}
          maxWidth="md"
          fullWidth
          container={container}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Localização — {mapCaixaName}
            </Typography>
            <IconButton onClick={() => setMapModalOpen(false)} size="small">
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 2, pb: 2, pt: 0 }}>
            <CaixaMapComponent
              latitude={mapCoords.latitude}
              longitude={mapCoords.longitude}
              caixaName={mapCaixaName}
              address={mapAddress}
              height={380}
            />
            <Box display="flex" justifyContent="center" mt={1}>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                {mapCoords.latitude.toFixed(6)}, {mapCoords.longitude.toFixed(6)}
                {' • '}
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    const url = `https://www.google.com/maps?q=${mapCoords.latitude},${mapCoords.longitude}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  sx={{ textTransform: 'none', p: 0, minWidth: 'auto', fontSize: 'inherit', verticalAlign: 'baseline' }}
                >
                  Abrir no Google Maps
                </Button>
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {(selectedCaixa?.pon_port?.id ?? selectedCaixa?.port_id) && (
        <CtosModal
          open={ctosModalOpen}
          onClose={() => setCtosModalOpen(false)}
          cardSlot={selectedCaixa?.pon_port?.slot}
          portNumber={selectedCaixa?.pon_port?.sfp || selectedCaixa?.pon_port?.portNumber}
          portId={selectedCaixa?.pon_port?.id ?? selectedCaixa?.port_id}
        />
      )}
    </>
  );
};

export default CtoMonitoramentoDetailsModal;
